import { v, type Infer } from "convex/values";
import type { Doc } from "./_generated/dataModel";
import type { GameTag } from "./tags";

export const ROW_TOOLS = ["optimizer", "sniper", "fantasy", "whale", "discrepancies"] as const;
export const LEG_TOOLS = ["arbitrage", "lowhold"] as const;

const rowToolValidator = v.union(v.literal("optimizer"), v.literal("sniper"), v.literal("fantasy"), v.literal("whale"), v.literal("discrepancies"));
const legToolValidator = v.union(v.literal("arbitrage"), v.literal("lowhold"));
export const shareToolValidator = v.union(rowToolValidator, legToolValidator);

const linksValidator = v.object({ generic: v.union(v.string(), v.null()), mobile: v.union(v.string(), v.null()), desktop: v.union(v.string(), v.null()) });

const heading = {
  league: v.string(),
  eventId: v.string(),
  matchup: v.string(),
  startsAt: v.number(),
  title: v.string(),
  href: v.string(),
  capturedAt: v.number(),
  art: v.array(v.string()),
  tag: v.optional(v.string()),
};

export const playShareValidator = v.object({
  kind: v.literal("play"),
  tool: rowToolValidator,
  ...heading,
  leagueId: v.string(),
  rowId: v.string(),
  side: v.optional(v.string()),
  line: v.string(),
  book: v.string(),
  bookId: v.string(),
  odds: v.number(),
  ev: v.number(),
  hit: v.optional(v.number()),
  limit: v.optional(v.string()),
  whale: v.optional(v.number()),
  lines: v.optional(v.object({ min: v.number(), max: v.number() })),
  links: linksValidator,
});

export const legValidator = v.object({ label: v.string(), book: v.string(), bookId: v.string(), odds: v.number(), links: linksValidator });

export const arbShareValidator = v.object({
  kind: v.literal("arb"),
  tool: legToolValidator,
  ...heading,
  market: v.string(),
  legs: v.array(legValidator),
  metric: v.union(v.object({ kind: v.literal("roi"), pct: v.number(), check: v.optional(v.literal(true)) }), v.object({ kind: v.literal("hold"), pct: v.number() })),
});

export const shareValidator = v.union(playShareValidator, arbShareValidator);

export type PlayShare = Infer<typeof playShareValidator>;
export type ArbShare = Infer<typeof arbShareValidator>;
export type ShareLeg = Infer<typeof legValidator>;
export type ShareLinks = Infer<typeof linksValidator>;
export type Share = Infer<typeof shareValidator>;
export type RowTool = PlayShare["tool"];
export type LegTool = ArbShare["tool"];
export type ShareTool = Share["tool"];
export type ShareProductKey = "ev-plan" | "whale-watch" | "ev-sniper";
export type LockedShare = { kind: "locked"; tool: ShareTool; requires: ShareProductKey };

export const SHARE_REQUIRES: Record<ShareTool, ShareProductKey> = {
  optimizer: "ev-plan",
  sniper: "ev-sniper",
  fantasy: "ev-plan",
  whale: "whale-watch",
  discrepancies: "ev-plan",
  arbitrage: "ev-plan",
  lowhold: "ev-plan",
};

const TEXT_MAX = 120;
const KEY_MAX = 300;
const HREF_MAX = 300;
const ODDS_MIN = 100;
const ODDS_MAX = 100_000;
const LEGS_MAX = 4;
const CAPTURE_MAX_AGE_MS = 24 * 60 * 60_000;
const CAPTURE_SKEW_MS = 60_000;
const LINK_MAX = 1000;
const SAME_ORIGIN_PATH_RE = /^\/(?!\/)[^\s\\]*$/;
const LINK_RE = /^(?!javascript:|data:|vbscript:)[a-z][a-z0-9+.-]*:\S+$/i;
const CANON_ID = "[0-9A-HJKMNP-TV-Z]{23}";
const ART_KEY_RE = new RegExp(`^(?:t/[a-z0-9_]{2,16}/oe_t_${CANON_ID}|p/[a-z0-9_]{2,16}/oe_t_${CANON_ID}/oe_p_${CANON_ID})/[0-9a-f]{16}\\.png$`);

export const ART_ORIGIN = "https://assets.oddsengine.dev/";

export function artKey(url: string | null | undefined): string | null {
  const key = url?.startsWith(ART_ORIGIN) ? url.slice(ART_ORIGIN.length) : null;
  return key !== null && ART_KEY_RE.test(key) ? key : null;
}

function isSaneArt(art: string[]) {
  return art.length <= 2 && art.every((key) => ART_KEY_RE.test(key)) && (art.length < 2 || art.every((key) => key.startsWith("t/")));
}

export function isText(value: string) {
  return value.trim().length > 0 && value.length <= TEXT_MAX;
}

export function isSameSitePath(value: string) {
  return value.length <= HREF_MAX && SAME_ORIGIN_PATH_RE.test(value);
}

function isAmerican(value: number) {
  return Number.isInteger(value) && Math.abs(value) >= ODDS_MIN && Math.abs(value) <= ODDS_MAX;
}

function saneLinks(links: ShareLinks) {
  return [links.generic, links.mobile, links.desktop].every((link) => link === null || (link.length <= LINK_MAX && LINK_RE.test(link)));
}

function isPercent(value: number, max: number) {
  return Number.isFinite(value) && value >= -100 && value <= max;
}

const EARLIEST_START = Date.UTC(2020, 0, 1);
const LATEST_START = Date.UTC(2100, 0, 1);

export function isSaneStart(ms: number): boolean {
  return Number.isSafeInteger(ms) && ms >= EARLIEST_START && ms <= LATEST_START;
}

export function isSaneShare(share: Share, now: number): boolean {
  const heading =
    [share.league, share.eventId, share.matchup, share.title].every(isText) &&
    isSaneStart(share.startsAt) &&
    Number.isSafeInteger(share.capturedAt) &&
    share.capturedAt >= now - CAPTURE_MAX_AGE_MS &&
    share.capturedAt <= now + CAPTURE_SKEW_MS &&
    isSameSitePath(share.href) &&
    isSaneArt(share.art) &&
    (share.tag === undefined || isText(share.tag));
  if (!heading) return false;
  if (share.kind === "arb") {
    return (
      isText(share.market) &&
      share.legs.length >= 2 &&
      share.legs.length <= LEGS_MAX &&
      share.legs.every((leg) => [leg.label, leg.book, leg.bookId].every(isText) && isAmerican(leg.odds) && saneLinks(leg.links)) &&
      isPercent(share.metric.pct, 100)
    );
  }
  return (
    [share.line, share.book, share.bookId, share.leagueId].every(isText) &&
    share.rowId.length > 0 &&
    share.rowId.length <= KEY_MAX &&
    (share.side === undefined || isText(share.side)) &&
    isAmerican(share.odds) &&
    isPercent(share.ev, 100_000) &&
    (share.hit === undefined || (share.hit > 0 && share.hit < 100)) &&
    (share.limit === undefined || isText(share.limit)) &&
    (share.whale === undefined || (Number.isFinite(share.whale) && share.whale > 0 && share.whale <= 1_000_000_000)) &&
    (share.lines === undefined || (Number.isFinite(share.lines.min) && Number.isFinite(share.lines.max) && share.lines.min <= share.lines.max)) &&
    saneLinks(share.links)
  );
}

export function shareLabels(share: Share): string[] {
  const heading = [share.league, share.matchup, share.title, ...(share.tag === undefined ? [] : [share.tag])];
  return share.kind === "arb" ? [...heading, share.market, ...share.legs.flatMap((leg) => [leg.label, leg.book])] : [...heading, share.line, share.book];
}

export function sameShare(a: Share | undefined, b: Share | undefined): boolean {
  if (!a || !b) return a === b;
  return JSON.stringify(shareLabels(a)) === JSON.stringify(shareLabels(b)) && a.tool === b.tool && a.eventId === b.eventId;
}

export function shareView(share: Share, entitlements: ReadonlySet<string>): Share | LockedShare {
  const requires = SHARE_REQUIRES[share.tool];
  return entitlements.has(requires) ? share : { kind: "locked", tool: share.tool, requires };
}

export function readerView(message: Doc<"messages">, entitlements: ReadonlySet<string>): { body: string; share?: Share | LockedShare; game?: GameTag } {
  const { body, share, tag } = message;
  if (!share) return { body, game: tag?.kind === "game" ? tag : undefined };
  const view = shareView(share, entitlements);
  return { body: view.kind === "locked" ? "" : body, share: view };
}
